import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WorkspaceContext = createContext(null);

export const WorkspaceProvider = ({ children }) => {
  const [tenant, setTenant] = useState(null);
  const [societes, setSocietes] = useState([]);
  const [activeSociete, setActiveSociete] = useState(null);
  const [selectedExploitations, setSelectedExploitations] = useState([]); // Multi-sélection
  const [exploitations, setExploitations] = useState([]);
  const [membership, setMembership] = useState(null);
  const [allTenants, setAllTenants] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch current workspace
  const fetchWorkspace = useCallback(async () => {
    const token = localStorage.getItem('token');
    console.log('[WorkspaceContext] fetchWorkspace called, token:', !!token);
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('[WorkspaceContext] Fetching /api/tenant/me...');
      const response = await fetch(`${API_URL}/api/tenant/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('[WorkspaceContext] Response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('[WorkspaceContext] Data received:', { tenant: data.tenant?.nom, societes: data.societes?.length });
        setTenant(data.tenant);
        setSocietes(data.societes || []);
        setMembership(data.membership);
        
        // Récupérer les accès de l'utilisateur pour connaître sa société par défaut
        let societeDefautId = null;
        let societesAutorisees = data.societes || [];
        
        try {
          const accesRes = await fetch(`${API_URL}/api/acces-collaborateurs/mon-acces`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (accesRes.ok) {
            const accesData = await accesRes.json();
            societeDefautId = accesData.societe_defaut_id;
            
            // Filtrer les sociétés autorisées si pas accès à toutes
            if (!accesData.acces_toutes_societes && accesData.societes_autorisees?.length > 0) {
              const idsAutorisees = accesData.societes_autorisees.map(s => s.id);
              societesAutorisees = (data.societes || []).filter(s => idsAutorisees.includes(s.id));
              setSocietes(societesAutorisees);
            }
          }
        } catch (e) {
          console.log('[WorkspaceContext] Pas de config accès spécifique');
        }
        
        // Set societe as active
        if (societesAutorisees?.length > 0) {
          // Priorité: 1. Société par défaut de l'utilisateur, 2. localStorage, 3. Pivot, 4. Première
          let selectedSociete = null;

          // 0. Société choisie dans cet onglet (survit au rechargement, pas à une nouvelle connexion)
          const choixOnglet = sessionStorage.getItem('activeSocieteIdOnglet');
          if (choixOnglet) selectedSociete = societesAutorisees.find(s => s.id === choixOnglet) || null;

          // 1. Société par défaut configurée pour l'utilisateur
          if (!selectedSociete && societeDefautId) {
            selectedSociete = societesAutorisees.find(s => s.id === societeDefautId);
          }
          
          // 2. Vérifier localStorage
          if (!selectedSociete) {
            const savedSocieteId = localStorage.getItem('activeSocieteId');
            selectedSociete = savedSocieteId ? societesAutorisees.find(s => s.id === savedSocieteId) : null;
          }
          
          // 3. Société pivot
          if (!selectedSociete) {
            selectedSociete = societesAutorisees.find(s => s.est_societe_pivot);
          }
          
          // 4. Première société
          if (!selectedSociete) {
            selectedSociete = societesAutorisees[0];
          }
          
          setActiveSociete(selectedSociete);
          localStorage.setItem('activeSocieteId', selectedSociete.id);
        }
      } else {
        console.log('[WorkspaceContext] Response not OK, keeping existing data');
        // Ne pas réinitialiser les données si la requête échoue
      }

      // Fetch exploitations
      const exploitationsRes = await fetch(`${API_URL}/api/referentiels/exploitations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (exploitationsRes.ok) {
        const exploitationsData = await exploitationsRes.json();
        setExploitations(exploitationsData.exploitations || []);
        
        // Restore selected exploitations from localStorage
        const savedExploitations = localStorage.getItem('selectedExploitations');
        if (savedExploitations) {
          try {
            const parsed = JSON.parse(savedExploitations);
            setSelectedExploitations(parsed);
          } catch (e) {
            // If no saved selection, select all by default
            setSelectedExploitations(exploitationsData.exploitations?.map(e => e.id) || []);
          }
        } else {
          // Select all by default
          setSelectedExploitations(exploitationsData.exploitations?.map(e => e.id) || []);
        }
      }
    } catch (error) {
      console.error('Erreur chargement workspace:', error);
    } finally {
      setLoading(false);
    }
  }, []); // Pas de dépendance pour éviter les re-renders

  // Fetch all tenants for the user
  const fetchAllTenants = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/tenant/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAllTenants(data.tenants || []);
      }
    } catch (error) {
      console.error('Erreur chargement tenants:', error);
    }
  }, []);

  // Switch to a different tenant
  const switchTenant = async (tenantId) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/tenant/switch/${tenantId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        await fetchWorkspace();
        toast.success('Workspace changé');
      }
    } catch (error) {
      console.error('Erreur changement workspace:', error);
      toast.error('Erreur lors du changement de workspace');
    }
  };

  // Switch active societe within current tenant
  const switchSociete = (societeId) => {
    const societe = societes.find(s => s.id === societeId);
    if (societe) {
      setActiveSociete(societe);
      localStorage.setItem('activeSocieteId', societeId);
      sessionStorage.setItem('activeSocieteIdOnglet', societeId);
      
      // Déclencher un événement custom pour notifier les autres composants (même onglet)
      window.dispatchEvent(new CustomEvent('societe-changed', { 
        detail: { societe_id: societeId, societe_nom: societe.nom }
      }));
      
      toast.success(`Société: ${societe.nom}`);
    }
  };

  // Toggle exploitation selection
  const toggleExploitation = (exploitationId) => {
    setSelectedExploitations(prev => {
      const newSelection = prev.includes(exploitationId)
        ? prev.filter(id => id !== exploitationId)
        : [...prev, exploitationId];
      
      localStorage.setItem('selectedExploitations', JSON.stringify(newSelection));
      return newSelection;
    });
  };

  // Select all exploitations
  const selectAllExploitations = () => {
    const allIds = exploitations.map(e => e.id);
    setSelectedExploitations(allIds);
    localStorage.setItem('selectedExploitations', JSON.stringify(allIds));
  };

  // Deselect all exploitations
  const deselectAllExploitations = () => {
    setSelectedExploitations([]);
    localStorage.setItem('selectedExploitations', JSON.stringify([]));
  };

  // Update tenant branding
  const updateBranding = async (brandingData) => {
    const token = localStorage.getItem('token');
    if (!token || !tenant) return;

    try {
      const response = await fetch(`${API_URL}/api/tenant/${tenant.id}/branding`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(brandingData)
      });
      
      if (response.ok) {
        await fetchWorkspace();
        toast.success('Personnalisation mise à jour');
        return true;
      }
    } catch (error) {
      console.error('Erreur mise à jour branding:', error);
      toast.error('Erreur lors de la mise à jour');
    }
    return false;
  };

  // Initial load and token change listener
  useEffect(() => {
    // Initial fetch only once
    let mounted = true;
    
    const init = async () => {
      if (mounted) {
        await fetchWorkspace();
        await fetchAllTenants();
      }
    };
    
    init();
    
    // Listen for token changes (storage events or custom event)
    const handleStorageChange = (e) => {
      if (e.key === 'token' && e.newValue && mounted) {
        console.log('[WorkspaceContext] Token changed, refetching workspace');
        fetchWorkspace();
        fetchAllTenants();
      }
    };
    
    // Custom event for same-tab token changes
    const handleTokenSet = () => {
      console.log('[WorkspaceContext] Custom token-set event received');
      if (mounted) {
        setTimeout(() => {
          fetchWorkspace();
          fetchAllTenants();
        }, 100);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('token-set', handleTokenSet);
    
    return () => {
      mounted = false;
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('token-set', handleTokenSet);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Note: La restauration de activeSociete est maintenant gérée directement dans fetchWorkspace
  // pour éviter les problèmes de timing et de re-renders

  const value = {
    tenant,
    societes,
    activeSociete,
    exploitations,
    selectedExploitations,
    membership,
    allTenants,
    loading,
    switchTenant,
    switchSociete,
    toggleExploitation,
    selectAllExploitations,
    deselectAllExploitations,
    updateBranding,
    refreshWorkspace: fetchWorkspace,
    refreshTenants: fetchAllTenants,
    isCreator: membership?.role === 'owner',
    hasWorkspace: !!tenant,
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
};
